import { DateTime } from 'luxon'
import { GTMReducedPromo } from '~/lib/models/gtm'
import {
  PromotionType,
  type promotionTypeType,
  type Promotion,
  PromotionDeeplinkTypes,
  PromotionDeeplinkType,
} from '~/lib/services/promos/promos.dto'
import { StoreOrder } from '~/lib/services/store/order/order.dto'
import { ProductDetailPromotion, ProductPromotion } from '~/lib/services/store/common.dto'
import { TimePeriod } from '~/lib/services/store/store.shared.dtos'
import { getWeeklyDay } from '~/utils/datesUtils'
import { arraysMatch } from '~/utils/arrayUtils'

export const promoErrorKey = (numberCode: number) => `promoError_${numberCode}`

export const parsePromotionForCart = (promo: Promotion) => {
  const applyDiscountPromotionOrder =
    promo.type === PromotionType.ORDER_AMOUNT || promo.type === PromotionType.ORDER_PERCENTAGE
  return {
    id: promo.promotionId,
    basicProperties: {
      enabled: promo.enabled,
    },
    criteria: {
      products: promo.productsRequired,
    },
    discount: {
      type: promo.type,
      amount: applyDiscountPromotionOrder ? promo.amount : promo.discount?.amount,
      products: promo.products?.length ? promo.products : undefined,
      montoMaxDiscount: promo.montoMaxDiscount ?? 0,
      all: promo.discount?.all,
      applyWithoutAllProducts: promo.discount?.applyWithoutAllProducts,
      qty: promo.discount?.qty,
    },
  }
}

export const getOrderPromoStr = (order: StoreOrder, onlyV2 = true) => {
  if (!onlyV2 && !order.promotions && !order?.promotionsV2) return undefined

  if (!onlyV2 && order.promotions?.length) {
    const promoLegacy = order.promotions?.[0]
    return promoLegacy.coupon
      ? `{shoppingCart.promotions.addPromo.prefix.coupon} <b>${promoLegacy.coupon}</b>`
      : `{shoppingCart.promotions.addPromo.prefix.employee}`
  }
  if (order.promotionsV2?.length) {
    const promoV2 = order.promotionsV2[0]
    return promoV2.code
      ? `{shoppingCart.promotions.addPromo.prefix.coupon} <b>${promoV2.code}</b>`
      : `{shoppingCart.promotions.addPromo.prefix.employee}`
  }
  return undefined
}

export const getOrderPromov2 = (order: StoreOrder): GTMReducedPromo | undefined => {
  if (order.promotionsV2?.length) {
    const { code, name, promotionSnapShotId: promotionId, type } = order.promotionsV2[0]
    return {
      code,
      name,
      promotionId,
      type,
    }
  }
  return undefined
}

export const VALID_PROMOS_BUBBLE: promotionTypeType[] = [
  PromotionType.PRODUCT_FIXED_PRICE,
  PromotionType.PRODUCT_PERCENTAGE,
  PromotionType.PRODUCT_AMOUNT,
]

export const getPromoLabel = (promo: ProductPromotion) => {
  const { format } = useFormatAmount()
  const { t } = useLocale()

  if (promo.prices?.priceWithDiscount === 0) return t('common.free')

  let label
  switch (promo.type) {
    case PromotionType.PRODUCT_AMOUNT:
      label = `${format(promo.amount!)} OFF`
      break
    case PromotionType.PRODUCT_FIXED_PRICE:
      label = t('common.offer')
      break
    case PromotionType.PRODUCT_PERCENTAGE:
      label = `${promo.amount}% OFF`
      break

    default:
      break
  }
  return label
}

const promoInSegmentation = (promoSegment: string[] | undefined, userSegmentation: string[]) => {
  if (!promoSegment || promoSegment.length === 0) return true
  const valid = arraysMatch<string>(promoSegment, userSegmentation)
  return valid
}

export const validPromoForBubble = (
  promotions: ProductPromotion[] | undefined,
  userSegmentation: string[]
): ProductPromotion | undefined | false => {
  if (!promotions || promotions.length === 0) return false

  const validPromo = promotions.find(
    promo =>
      VALID_PROMOS_BUBBLE.includes(promo.type as promotionTypeType) &&
      promoInSegmentation(promo.clientSegmentation ?? [], userSegmentation)
  )

  return validPromo && isPromotionActiveAtDateTime(validPromo) ? validPromo : false
}

export const validFreeShippingPromo = (
  promotions: ProductPromotion[],
  userSegmentation: string[]
): boolean => {
  if (!promotions || promotions.length === 0) return false

  const validPromo = promotions.find(
    promo =>
      promo.type === PromotionType.SHIPPING_PERCENTAGE &&
      promo.amount === 100 &&
      promoInSegmentation(promo.clientSegmentation ?? [], userSegmentation)
  )

  if (!validPromo) return false

  return isPromotionActiveAtDateTime(validPromo)
}

export const checkFreeShippingPromo = async (
  promotions: ProductPromotion[]
): Promise<ProductDetailPromotion | undefined> => {
  const { tryCheckPromos } = usePromotions()

  const promotion = promotions.find(promo => {
    const isShipping = promo.type === PromotionType.SHIPPING_PERCENTAGE
    // valid if clientSegmentation is undefined or empty
    const validSegmentation = !promo.clientSegmentation?.length
    const isActiveAtDateTime = isPromotionActiveAtDateTime(promo)
    return isShipping && validSegmentation && isActiveAtDateTime
  })

  if (!promotion) return undefined

  const responseCheckPromos = await tryCheckPromos({ promotionsId: [promotion.id] })

  if (!responseCheckPromos || !responseCheckPromos[0]) return undefined

  const { promotionId: id, type } = responseCheckPromos[0]
  return { id, type: type!, amount: 100 }
}

export const isPromotionActiveAtDateTime = (productPromotion: ProductPromotion): boolean => {
  const now = DateTime.now()
  const promotion = productPromotion
  if (!promotion) return false
  if (isPromotionActiveAtDateRange(promotion, now) && isPromotionInTimeSlotRange(promotion, now))
    return true
  return false
}

export const isPromotionActiveAtDateRange = (
  promotion: ProductPromotion,
  now: DateTime
): boolean => {
  const startDate = promotion.dateFrom ? DateTime.fromISO(promotion.dateFrom).startOf('day') : null
  const endDate = promotion.dateTo ? DateTime.fromISO(promotion.dateTo).startOf('day') : null
  const isStartDateValid = startDate?.isValid ?? false
  const isEndDateValid = endDate?.isValid ?? false
  const currentDate = now.startOf('day')

  // Case 1: Both dates are null → promotion should be active
  if (!startDate && !endDate) {
    return true
  }

  // Case 2: Only startDate is valid → active if startDate <= today
  if (isStartDateValid && startDate && !endDate) {
    return startDate <= currentDate
  }

  // Case 3: Only endDate is valid → active if today <= endDate
  if (isEndDateValid && endDate && !startDate) {
    return currentDate <= endDate
  }

  // Case 4: Both dates are valid → active if today is within the range
  if (startDate && isStartDateValid && endDate && isEndDateValid)
    return currentDate >= startDate && currentDate <= endDate

  // Fallback: promotion is not active
  return false
}

export const isPromotionInTimeSlotRange = (promotion: ProductPromotion, now: DateTime) => {
  if (!promotion.timeSlotsService) return false
  // Current day in 'mon', 'tue', etc. format
  const currentDayKey = getWeeklyDay(now)
  // Check if there is a configuration for the current day
  const currentDayConfig = promotion.timeSlotsService?.daysOfWeek?.find(
    d => d.day === currentDayKey
  )
  if (!currentDayConfig || currentDayConfig.timePeriods.length === 0) return false
  // Ensure there are defined time periods for the current day
  if (!Array.isArray(currentDayConfig.timePeriods) || currentDayConfig.timePeriods.length === 0)
    return false
  // Check if the current time falls within any of the configured time slots for today
  return currentDayConfig.timePeriods.some(period => isNowInTimePeriod(period, now))
}

const isNowInTimePeriod = (period: TimePeriod, now: DateTime) => {
  const [openHour, openMin] = period.hourOpen.split(':').map(Number)
  const [closeHour, closeMin] = period.hourClose.split(':').map(Number)
  // Start time when the promotion becomes active
  const promoStartTime = now.set({ hour: openHour, minute: openMin, second: 0 })
  // End time when the promotion expires
  const promoEndTime = now.set({ hour: closeHour, minute: closeMin, second: 0 })
  // Handle ranges that cross midnight (e.g., 22:00 - 02:00)
  if (promoEndTime < promoStartTime) {
    return now >= promoStartTime || now <= promoEndTime
  } else {
    // Standard case: check if current time is between start and end time
    return now >= promoStartTime && now <= promoEndTime
  }
}

/**
 * Determines the deeplink type from a promotion type.
 *
 * This function receives a promotion type from the backend (ORDER_*, PRODUCT_*,
 * SHIPPING_*, etc.) and returns only the deeplink types supported by the app:
 * "ORDER" or "PRODUCT".
 *
 * - If the type starts with "ORDER", it returns "ORDER".
 * - If the type starts with "PRODUCT", it returns "PRODUCT".
 * - If it does not match either (e.g., SHIPPING), it returns `undefined`
 *   so the caller can safely stop the process.
 *
 * @param type Full promotion type string (e.g. "ORDER_AMOUNT", "PRODUCT_PERCENTAGE").
 * @returns "ORDER", "PRODUCT" or `undefined` if not applicable.
 */
export const getPromotionTypeToDeeplink = (
  type: keyof typeof PromotionType
): PromotionDeeplinkTypes | undefined => {
  if (type.startsWith(PromotionDeeplinkType.ORDER)) return PromotionDeeplinkType.ORDER
  if (type.startsWith(PromotionDeeplinkType.PRODUCT)) return PromotionDeeplinkType.PRODUCT
}
